/**
 * @fileoverview Bibliography markers for the report editor (MOR-924)
 * @module jobs/phases/reporting/citation-markers
 *
 * Purpose:
 * - Show citations in the editable report the way the preview shows them: a
 *   superscript number keyed to the "Sources" list, with the legacy source
 *   title hidden rather than sitting inline in the sentence.
 *
 * The read-only renderer does this by rewriting the rendered DOM
 * (`numberCitationsInDom`), which is safe because nothing there is editable.
 * The editor cannot: ProseMirror reads DOM changes back into the document, so
 * a display-only rewrite would dirty the report and could be saved. Everything
 * here is therefore a decoration — drawn over the document, never part of it.
 *
 * Used by: ReportEditor
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Mark, Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { CitedSource } from "@/components/agents/universal/sourceCitations.helpers";
import {
  CITE_TITLE_CLASS,
  citationMarkerElement,
  sourceKey,
} from "./reportSources";

const pluginKey = new PluginKey<DecorationSet>("reportCitationMarkers");

/** One stretch of text carrying a single citation mark. */
type Run = { from: number; to: number; mark: Mark };

/** The shared marker element, opted out of editing. */
function markerElement(marker: string, sources: CitedSource[]): HTMLElement {
  const el = citationMarkerElement(marker, sources);
  // Decorations sit inside the editable surface, so opt the marker out of
  // editing — otherwise a caret can land in it and typing would look lost.
  el.contentEditable = "false";
  return el;
}

/**
 * Number every citation in `doc` by the first appearance of each source (the
 * same order `buildCitationNumbers` uses for the bibliography) and return the
 * decorations that draw them.
 */
function buildDecorations(doc: ProseMirrorNode): DecorationSet {
  const numbers = new Map<string, number>();
  const decorations: Decoration[] = [];
  let run: Run | null = null;

  const closeRun = () => {
    if (!run) return;
    const { from, to, mark } = run;
    run = null;

    const sources = (mark.attrs.sources ?? []) as CitedSource[];
    const marker = sources
      .map((source) => {
        const key = sourceKey(source);
        const existing = numbers.get(key);
        if (existing) return existing;
        const next = numbers.size + 1;
        numbers.set(key, next);
        return next;
      })
      .join(",");
    if (!marker) return;

    // Legacy citations are the source title itself; the marker replaces it.
    if (!mark.attrs.citeText) {
      decorations.push(
        Decoration.inline(from, to, { class: CITE_TITLE_CLASS }),
      );
    }
    // `marks: []` keeps the marker out of the citation's own span, so hiding a
    // legacy source title never hides its marker too.
    decorations.push(
      Decoration.widget(to, () => markerElement(marker, sources), {
        side: 1,
        marks: [],
      }),
    );
  };

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const mark = node.marks.find((m) => m.type.name === "reportCitation");
    if (!mark) {
      closeRun();
      return;
    }
    // A citation split across text nodes (say by a bold word) is one citation
    // and gets one marker, so extend the open run instead of starting another.
    if (run && run.to === pos && run.mark.eq(mark)) {
      run.to = pos + node.nodeSize;
      return;
    }
    closeRun();
    run = { from: pos, to: pos + node.nodeSize, mark };
  });
  closeRun();

  return DecorationSet.create(doc, decorations);
}

/**
 * Draws citation markers in the editable report. Add alongside the citation
 * mark; the read-only renderer must not use it (it numbers the DOM directly,
 * and would end up with two markers per citation).
 */
export const ReportCitationMarkers = Extension.create({
  name: "reportCitationMarkers",
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: pluginKey,
        state: {
          init: (_config, state) => buildDecorations(state.doc),
          // Numbering shifts when citations are added or removed, so rebuild
          // rather than map the existing decorations forward.
          apply: (tr, current) =>
            tr.docChanged ? buildDecorations(tr.doc) : current,
        },
        props: {
          decorations: (state) => pluginKey.getState(state),
        },
      }),
    ];
  },
});
